import React, { useEffect, useState } from 'react';

import { Spin } from 'antd';

/**
 * Verifica se uma URL de imagem é válida e acessível
 * @param url URL da imagem para validar
 * @returns Promise<boolean> - true se a imagem é válida e acessível, false caso contrário
 */
export const isValidImageUrl = async (url: string): Promise<boolean> => {
  if (!url) return false;

  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('content-type');
    return Boolean(response.ok && contentType?.startsWith('image/'));
  } catch (error) {
    console.error('Erro ao validar URL da imagem:', error);
    return false;
  }
};

/**
 * Hook para gerenciar o estado de uma imagem e sua validação
 * @param imageUrl URL da imagem para validar
 * @returns Objeto com o estado da imagem e funções de validação
 */
export const useImageValidation = (imageUrl: string | null) => {
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateImage = async () => {
      if (!imageUrl) {
        setIsValid(false);
        setIsLoading(false);
        setError('URL da imagem não fornecida');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const isValid = await isValidImageUrl(imageUrl);
        setIsValid(true);
        if (!isValid) {
          setError('Imagem inválida ou inacessível');
        }
      } catch (error) {
        setIsValid(false);
        setError('Erro ao validar imagem');
        console.error('Erro ao validar imagem:', error);
      } finally {
        setIsLoading(false);
      }
    };

    validateImage();
  }, [imageUrl]);

  return {
    isValid,
    isLoading,
    error,
  };
};

/**
 * Componente de fallback para quando a imagem não está disponível
 */
export const ImageFallback: React.FC<{ size?: number }> = ({ size = 180 }) => {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666',
        fontSize: '14px',
      }}
    >
      Sem imagem
    </div>
  );
};

/**
 * Componente de imagem com validação e fallback
 */
export const ValidatedImage: React.FC<{
  src: string | null;
  alt: string;
  size?: number;
  style?: React.CSSProperties;
}> = ({ src, alt, size = 180, style }) => {
  const { isValid, isLoading } = useImageValidation(src);

  if (isLoading) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="small" />
      </div>
    );
  }

  if (!isValid || !src) {
    return <ImageFallback size={size} />;
  }

  return (
    <img
      src={src}
      alt={alt}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'cover',
        ...style,
      }}
      onError={() => {
        console.error(`Erro ao carregar imagem: ${src}`);
      }}
    />
  );
};
